import { useTranslation } from "react-i18next";
import { App } from "@/lib/capacitor/app";

interface CurrentDeviceCardProps {
  isEnabled: boolean;
  permissionStatus?: string | null;
  onToggle: () => void;
  isLoading?: boolean;
}

export function CurrentDeviceCard({
  isEnabled,
  permissionStatus,
  onToggle,
  isLoading,
}: CurrentDeviceCardProps) {
  const { t } = useTranslation();

  const isDenied = permissionStatus === "denied";

  // Get appropriate styling based on state
  const bgColors = isDenied
    ? "bg-gradient-to-br from-error-10 to-error-20"
    : isEnabled
      ? "bg-gradient-to-br from-success-10 to-success-20"
      : "bg-gradient-to-br from-surface-primary to-surface-secondary";

  const iconColors = isDenied
    ? "from-error-80 to-error-100"
    : isEnabled
      ? "from-success-80 to-success-100"
      : "from-neutral-400 to-neutral-500";

  const textColors = isDenied
    ? {
        title: "text-error-120",
        subtitle: "text-error-120",
      }
    : isEnabled
      ? {
          title: "text-success-120",
          subtitle: "text-success-120",
        }
      : {
          title: "text-txt-primary",
          subtitle: "text-txt-primary",
        };

  const getTitle = () => {
    if (isDenied) {
      return t("settings.notifications.status.denied.title");
    }
    if (isEnabled) {
      return t("settings.notifications.status.active.title");
    }
    return t("settings.notifications.status.inactive.title");
  };

  const getSubtitle = () => {
    if (isDenied) {
      return t("settings.notifications.status.denied.description");
    }
    if (isEnabled) {
      return t("settings.notifications.status.active.description");
    }
    return t("settings.notifications.status.inactive.description");
  };

  const getButtonText = () => {
    if (isDenied) {
      return t("settings.notifications.status.denied.actions.open-settings");
    }
    if (isEnabled) {
      return t("settings.notifications.status.active.actions.disable");
    }
    return t("settings.notifications.status.inactive.actions.enable");
  };

  const handleButtonClick = () => {
    if (isDenied) {
      App.openSettings();
    } else {
      onToggle();
    }
  };

  return (
    <div className={`overflow-hidden rounded-xl ${bgColors}`}>
      <div className="relative flex items-start gap-4 p-4">
        {/* Icon with pulse effect for enabled state */}
        <div className="relative flex-shrink-0">
          {isEnabled && !isDenied && (
            <div className="absolute inset-0 animate-pulse rounded-full opacity-20" />
          )}
          <div
            className={`relative flex size-12 items-center justify-center rounded-full bg-gradient-to-br ${iconColors} shadow-lg`}
          >
            <svg
              className="size-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isDenied ? (
                // Bell with slash for denied
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.586 15H4a1 1 0 01-.707-1.707l1.586-1.586a1 1 0 00.293-.707V8.5a5.5 5.5 0 0111 0v2.5a1 1 0 00.293.707l1.586 1.586A1 1 0 0118 15h-1.586M12 19a2 2 0 01-2-2h4a2 2 0 01-2 2zM3 3l18 18"
                />
              ) : isEnabled ? (
                // Bell with checkmark for enabled
                <>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 12l2 2 4-4"
                    className="text-white"
                  />
                </>
              ) : (
                // Regular bell for disabled
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              )}
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <h3 className={`text-base font-semibold ${textColors.title}`}>{getTitle()}</h3>
            {isEnabled && !isDenied && (
              <span className="rounded-full bg-success-10 px-2 py-0.5 text-xs font-medium text-success-120">
                {t("settings.notifications.badge.active")}
              </span>
            )}
          </div>
          <p className={`mb-3 text-sm ${textColors.subtitle}`}>{getSubtitle()}</p>

          {/* Action button */}
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={isLoading}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 ${
              isDenied
                ? "bg-error-100 hover:bg-error-80"
                : isEnabled
                  ? "bg-success-100 hover:bg-success-80"
                  : "bg-brand-600 hover:bg-brand-700"
            }`}
          >
            {isLoading && (
              <svg
                className="size-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isLoading ? t("common:loading") : getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
}
